// EditRestaurant.tsx

/* eslint-disable camelcase */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Button,
  Container,
  Flex,
  Heading,
  Icon,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  useToast,
  VStack,
  HStack,
  Text,
  useColorModeValue,
  Spinner,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Badge,
  Tooltip,
  IconButton,
} from '@chakra-ui/react';
import 'leaflet/dist/leaflet.css';
import {
  useUpdateRestaurantMutation,
  useGetRestaurantByPkQuery,
  Restaurant,
  useGetRestaurantCategoryDishesQuery,
  Order_By,
} from '../utils/__generated__/graphql';
import { useParams, useNavigate } from 'react-router-dom';
import { useGetImageUrl, useSettlementData } from 'src/hooks';
import { CategorizedFoods, EditDetails } from './shared';
import { calculateDateRange, compressImage, timeRanges } from 'src/utils';
import SettledOrders from './shared/SettledOrders';
import UnsettledOrders from './shared/UnsettledOrders';
import { FiArrowLeft, FiSave, FiRefreshCw, FiPackage, FiDollarSign, FiInfo, FiShoppingBag } from 'react-icons/fi';
import { motion } from 'framer-motion';
import { nhost } from 'src/lib/nhost';

const MotionBox = motion(Box);
const MotionFlex = motion(Flex);

// Constants
const FIELD_NAMES = [
  'name',
  'address',
  'phone',
  'email',
  'free_distance',
  'max_distance',
  'admin_commission',
  'delivery_commission',
  'delivery_charge',
  'specialtyId',
  'type',
  'location',
  'isActive',
  'description',
  'short_description',
  'threshold_amount',
  'min_order_amount',
  'opening_hour',
  'closing_hour',
  'weekly_off_days',
] as const;

const TAB_INDEXES = {
  DETAILS: 0,
  MENU: 1,
  UNSETTLED: 2,
  SETTLED: 3,
} as const;

// Custom hooks
const useRestaurantData = (restaurantId: string) => {
  const { data, loading, error, refetch } = useGetRestaurantByPkQuery({
    variables: { id: restaurantId },
  });

  const { data: restaurantCategoryDishesData, refetch: refetchDishes } = useGetRestaurantCategoryDishesQuery({
    variables: {
      category_order_by: [{ name: Order_By.Asc }],
      dish_order_by: [{ name: Order_By.Asc }],
      id: restaurantId,
    },
  });

  return {
    restaurantData: data?.restaurant_by_pk as Restaurant,
    restaurantCategoryDishesData,
    loading,
    error,
    refetch,
    refetchDishes,
  };
};

const useFormState = (restaurantData: Restaurant | undefined) => {
  const [formValues, setFormValues] = useState<Array<{ label: string; value: any }>>(
    FIELD_NAMES.map((fieldName) => ({
      label: fieldName,
      value: fieldName === 'weekly_off_days' ? [] : '',
    }))
  );

  useEffect(() => {
    if (restaurantData) {
      setFormValues((prevValues) =>
        prevValues.map((field) => ({
          ...field,
          value:
            field.label === 'weekly_off_days'
              ? restaurantData[field.label] || []
              : (restaurantData as any)[field.label],
        }))
      );
    }
  }, [restaurantData]);

  const handleInputChange = useCallback(
    (label: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | string[] | Date) => {
      let value: any;

      if (Array.isArray(e)) {
        value = e;
      } else if (e instanceof Date) {
        value = e.toTimeString().slice(0, 5);
      } else {
        value = e.target.value;
      }

      setFormValues((prevValues) => prevValues.map((field) => (field.label === label ? { ...field, value } : field)));
    },
    []
  );

  const hasFormChanged = useCallback(() => {
    if (!restaurantData) return false;
    return formValues.some((field) => field.value !== ((restaurantData as any)[field.label] || ''));
  }, [formValues, restaurantData]);

  return {
    formValues,
    handleInputChange,
    hasFormChanged,
  };
};

const useTimeRange = () => {
  const [timeRange, setTimeRange] = useState<string>(() => {
    return localStorage.getItem('timeRange') || 'today';
  });

  const { startDate, endDate } = calculateDateRange(timeRange);

  const handleTimeRangeChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    const newTimeRange = e.target.value;
    setTimeRange(newTimeRange);
    localStorage.setItem('timeRange', newTimeRange);
  }, []);

  return {
    timeRange,
    startDate,
    endDate,
    handleTimeRangeChange,
  };
};

const useActiveTab = () => {
  const [activeTab, setActiveTab] = useState(() => {
    const savedTab = localStorage.getItem('editRestaurantActiveTab');
    return savedTab ? Number(savedTab) : TAB_INDEXES.DETAILS;
  });

  const handleActiveTab = useCallback((index: number) => {
    setActiveTab(index);
    localStorage.setItem('editRestaurantActiveTab', index.toString());
  }, []);

  return {
    activeTab,
    handleActiveTab,
  };
};

// Components
const LoadingSpinner = () => {
  const textColor = useColorModeValue('gray.600', 'gray.300');

  return (
    <Flex justify="center" align="center" minH="60vh">
      <VStack spacing={4}>
        <Spinner size="xl" color="brand.500" thickness="4px" />
        <Text color={textColor}>Loading restaurant details...</Text>
      </VStack>
    </Flex>
  );
};

const ErrorAlert = ({ error }: { error: any }) => (
  <Alert status="error" variant="left-accent" borderRadius="md">
    <AlertIcon />
    <AlertTitle>Error loading restaurant data</AlertTitle>
    <AlertDescription>{error.message}</AlertDescription>
  </Alert>
);

const Header = ({
  restaurantData,
  loading,
  refetch,
  navigate,
}: {
  restaurantData: Restaurant | undefined;
  loading: boolean;
  refetch: () => void;
  navigate: (path: number) => void;
}) => {
  const textColor = useColorModeValue('gray.600', 'gray.300');

  return (
    <Flex
      direction={{ base: 'column', md: 'row' }}
      justify="space-between"
      align={{ base: 'start', md: 'center' }}
      mb={8}
      gap={4}
    >
      <Box>
        <HStack mb={2}>
          <Heading size="lg">Edit Restaurant</Heading>
          <Badge colorScheme="brand" fontSize="md" borderRadius="full" px={3}>
            {restaurantData?.name}
          </Badge>
        </HStack>
        <Text color={textColor}>Update restaurant information and manage menu items</Text>
      </Box>
      <HStack spacing={4}>
        <Tooltip label="Refresh data">
          <IconButton
            aria-label="Refresh"
            icon={<Icon as={FiRefreshCw as any} />}
            isLoading={loading}
            onClick={() => refetch()}
            variant="ghost"
          />
        </Tooltip>
        <Button
          leftIcon={<Icon as={FiArrowLeft as any} />}
          onClick={() => navigate(-1)}
          variant="outline"
          colorScheme="gray"
          size="lg"
        >
          Go Back to Restaurants
        </Button>
      </HStack>
    </Flex>
  );
};

const StatsSection = ({
  totalCategories,
  totalDishes,
  totalUnsettledOrders,
  totalSettledOrders,
}: {
  totalCategories: number;
  totalDishes: number;
  totalUnsettledOrders: number;
  totalSettledOrders: number;
}) => {
  const statsBg = useColorModeValue('white', 'gray.700');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const textColor = useColorModeValue('gray.600', 'gray.300');
  const accentColor = useColorModeValue('brand.500', 'brand.300');

  const stats = [
    {
      label: 'Categories',
      value: totalCategories,
      icon: FiPackage,
      description: 'Menu categories',
    },
    {
      label: 'Total Dishes',
      value: totalDishes,
      icon: FiPackage,
      description: 'Available food items',
    },
    {
      label: 'Unsettled Orders',
      value: totalUnsettledOrders,
      icon: FiShoppingBag,
      description: 'Pending settlement',
    },
    {
      label: 'Settled Orders',
      value: totalSettledOrders,
      icon: FiDollarSign,
      description: 'Completed settlements',
    },
  ];

  return (
    <SimpleGrid columns={{ base: 1, md: 4 }} spacing={6} mb={8}>
      {stats.map((stat, index) => (
        <MotionFlex
          key={stat.label}
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5, delay: 0.2 + index * 0.1 }}
        >
          <Stat
            px={6}
            py={4}
            bg={statsBg}
            borderRadius="xl"
            boxShadow="sm"
            border="1px"
            borderColor={borderColor}
            _hover={{ transform: 'translateY(-2px)', boxShadow: 'md' }}
            transition="all 0.2s"
          >
            <StatLabel fontSize="sm" fontWeight="medium" color={textColor}>
              {stat.label}
            </StatLabel>
            <StatNumber fontSize="3xl" fontWeight="bold" color={accentColor}>
              {stat.value}
            </StatNumber>
            <StatHelpText>
              <HStack>
                <Icon as={stat.icon as any} />
                <Text>{stat.description}</Text>
              </HStack>
            </StatHelpText>
          </Stat>
        </MotionFlex>
      ))}
    </SimpleGrid>
  );
};

const TabContent = ({
  activeTab,
  formValues,
  handleInputChange,
  logoUrl,
  coverUrl,
  handleFileChange,
  hasFormChanged,
  updateLoading,
  handleSubmit,
  restaurantCategoryDishesData,
  refetchDishes,
  unsettledData,
  settledData,
  timeRange,
  handleTimeRangeChange,
}: {
  activeTab: number;
  formValues: Array<{ label: string; value: any }>;
  handleInputChange: (label: string) => (e: any) => void;
  logoUrl: string | undefined;
  coverUrl: string | undefined;
  handleFileChange: (event: any, type: string) => void;
  hasFormChanged: () => boolean;
  updateLoading: boolean;
  handleSubmit: (e: React.FormEvent) => void;
  restaurantCategoryDishesData: any;
  refetchDishes: () => void;
  unsettledData: any;
  settledData: any;
  timeRange: string;
  handleTimeRangeChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
}) => {
  const tabsBg = useColorModeValue('white', 'gray.800');

  return (
    <Tabs variant="soft-rounded" colorScheme="brand" index={activeTab}>
      <Box
        borderRadius="xl"
        overflow="hidden"
        backgroundColor={tabsBg}
        shadow="sm"
        p={4}
        alignItems="center"
        display="flex"
      >
        <TabList>
          <Tab>
            <HStack spacing={2}>
              <Icon as={FiInfo as any} />
              <Text>Details</Text>
            </HStack>
          </Tab>
          <Tab>
            <HStack spacing={2}>
              <Icon as={FiPackage as any} />
              <Text>Menu Items</Text>
            </HStack>
          </Tab>
          <Tab>
            <HStack spacing={2}>
              <Icon as={FiShoppingBag as any} />
              <Text>Unsettled</Text>
            </HStack>
          </Tab>
          <Tab>
            <HStack spacing={2}>
              <Icon as={FiDollarSign as any} />
              <Text>Settled</Text>
            </HStack>
          </Tab>
        </TabList>
      </Box>

      <TabPanels>
        <TabPanel px={0}>
          <EditDetails
            formValues={formValues}
            handleInputChange={handleInputChange}
            logoUrl={logoUrl}
            coverUrl={coverUrl}
            handleFileChange={handleFileChange}
          />
          <Box mt={4} display="flex" justifyContent="flex-end">
            {activeTab === TAB_INDEXES.DETAILS && (
              <Button
                leftIcon={<Icon as={FiSave as any} />}
                colorScheme="brand"
                size="lg"
                onClick={handleSubmit}
                isDisabled={!hasFormChanged()}
                isLoading={updateLoading}
              >
                Save Changes
              </Button>
            )}
          </Box>
        </TabPanel>

        <TabPanel px={0}>
          <CategorizedFoods dishesData={restaurantCategoryDishesData} refetch={refetchDishes} />
        </TabPanel>

        <TabPanel px={0}>
          <UnsettledOrders data={unsettledData} />
        </TabPanel>

        <TabPanel px={0}>
          <SettledOrders
            data={settledData}
            timeRange={timeRange}
            handleTimeRangeChange={handleTimeRangeChange}
            timeRanges={timeRanges}
          />
        </TabPanel>
      </TabPanels>
    </Tabs>
  );
};

// Main component
export const EditRestaurant = () => {
  const { id: restaurantId } = useParams();
  const navigate = useNavigate();
  const toast = useToast();

  // Custom hooks
  const { restaurantData, restaurantCategoryDishesData, loading, error, refetch, refetchDishes } = useRestaurantData(
    restaurantId!
  );
  const { formValues, handleInputChange, hasFormChanged } = useFormState(restaurantData);
  const { timeRange, startDate, endDate, handleTimeRangeChange } = useTimeRange();
  const { activeTab } = useActiveTab();

  // Settlement data
  const { unsettledData, settledData } = useSettlementData(startDate, endDate, restaurantId);

  // Update mutation
  const [updateRestaurant, { loading: updateLoading }] = useUpdateRestaurantMutation({
    onCompleted: () => refetch(),
  });

  // Image URLs
  const { imageUrl: logoUrl } = useGetImageUrl(restaurantData?.image);
  const { imageUrl: coverUrl } = useGetImageUrl(restaurantData?.cover);

  // Memoized calculations
  const stats = useMemo(() => {
    const totalDishes =
      restaurantCategoryDishesData?.category?.reduce(
        (acc, cat) => acc + (cat?.dishes_aggregate?.aggregate?.count || 0),
        0
      ) || 0;

    const totalCategories = restaurantCategoryDishesData?.category?.length || 0;
    const totalUnsettledOrders = unsettledData?.orders?.length || 0;
    const totalSettledOrders = settledData?.settlements?.length || 0;

    return {
      totalDishes,
      totalCategories,
      totalUnsettledOrders,
      totalSettledOrders,
    };
  }, [restaurantCategoryDishesData, unsettledData, settledData]);

  // Event handlers
  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();

      if (!hasFormChanged()) {
        return;
      }

      const updatedValues = formValues.reduce(
        (acc, field) => {
          if (field.label === 'location' && field.value) {
            if (
              typeof field.value === 'object' &&
              field.value.type === 'Point' &&
              Array.isArray(field.value.coordinates) &&
              field.value.coordinates.length === 2
            ) {
              acc[field.label] = {
                type: 'Point',
                coordinates: [Number(field.value.coordinates[0]), Number(field.value.coordinates[1])],
              };
            }
          } else {
            acc[field.label] = field.value;
          }
          return acc;
        },
        {} as Record<string, any>
      );

      try {
        await updateRestaurant({
          variables: {
            pk_columns: { id: restaurantId },
            _set: updatedValues,
          },
        });

        toast({
          title: 'Restaurant Updated successfully',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      } catch (error: any) {
        toast({
          title: 'Failed to update Restaurant',
          description: error?.message || 'An error occurred',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
        console.error('Error updating restaurant:', error?.message);
      }
    },
    [formValues, hasFormChanged, updateRestaurant, restaurantId, toast]
  );

  // Memoized file change handler
  const handleFileChange = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>, type: string) => {
      const file = event.target.files?.[0];
      if (!file || !restaurantData) return;

      const imageType = type === 'logo' ? 'image' : 'cover';
      const previousFileId = type === 'logo' ? restaurantData?.image : restaurantData?.cover;

      try {
        if (previousFileId) {
          await nhost.storage.delete({ fileId: previousFileId });
        }

        const compressedFile = await compressImage(file);
        const result = await nhost.storage.upload({
          file: compressedFile,
          bucketId: 'default',
        });

        if (result) {
          await updateRestaurant({
            variables: {
              pk_columns: { id: restaurantData?.id },
              _set: {
                [imageType]: result?.fileMetadata?.id ?? null,
              },
            },
          });
          toast({
            title: 'Image Updated',
            status: 'success',
            duration: 2000,
          });
        }
      } catch (error: any) {
        toast({
          title: `Failed to upload ${type}`,
          description: error?.message,
          status: 'error',
          duration: 3000,
        });
      }
    },
    [restaurantData, updateRestaurant, toast]
  );

  // Loading and error states
  if (loading) {
    return <LoadingSpinner />;
  }

  if (error) {
    return <ErrorAlert error={error} />;
  }

  return (
    <Container maxW="8xl" p={{ base: 4, md: 8 }}>
      <MotionBox initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
        <Header restaurantData={restaurantData} loading={loading} refetch={refetch} navigate={navigate} />

        <StatsSection
          totalCategories={stats.totalCategories}
          totalDishes={stats.totalDishes}
          totalUnsettledOrders={stats.totalUnsettledOrders}
          totalSettledOrders={stats.totalSettledOrders}
        />

        <TabContent
          activeTab={activeTab}
          formValues={formValues}
          handleInputChange={handleInputChange}
          logoUrl={logoUrl}
          coverUrl={coverUrl}
          handleFileChange={handleFileChange}
          hasFormChanged={hasFormChanged}
          updateLoading={updateLoading}
          handleSubmit={handleSubmit}
          restaurantCategoryDishesData={restaurantCategoryDishesData}
          refetchDishes={refetchDishes}
          unsettledData={unsettledData}
          settledData={settledData}
          timeRange={timeRange}
          handleTimeRangeChange={handleTimeRangeChange}
        />
      </MotionBox>
    </Container>
  );
};
